import React, { useState } from "react";
import { FaFileDownload, FaFileInvoice, FaReceipt } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  downloadInvoicePdf,
  downloadReceiptPdf,
  hasInvoiceDoc,
  invoiceIdOf,
  type InvoiceDoc,
  type ReceiptDoc,
} from "../Services/billing";

const currency = (amt?: number | null) =>
  typeof amt === "number" && !Number.isNaN(amt)
    ? `₹${amt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    : "—";

interface Props {
  saleOrderId: number;
  invoice?: InvoiceDoc | null;
  invoiceId?: number | null;
  receipts?: ReceiptDoc[] | null;
  isCustomer?: boolean;
  compact?: boolean;
}

const btnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  background: "#fff",
  borderRadius: 8,
  outline: "1px solid #D1D5DB",
  border: "none",
  color: "#374151",
  fontSize: 14,
  fontWeight: 500,
  padding: "8px 14px",
  cursor: "pointer",
};

const BillingDocuments: React.FC<Props> = ({
  saleOrderId,
  invoice,
  invoiceId,
  receipts,
  isCustomer = false,
  compact = false,
}) => {
  const [busy, setBusy] = useState<string | null>(null);
  const invPresent = hasInvoiceDoc(invoice, invoiceId);
  const invId = invoiceIdOf(invoice, invoiceId);
  const list = Array.isArray(receipts) ? receipts : [];

  if (!invPresent && list.length === 0) {
    return (
      <div
        style={{
          background: "#F9FAFB",
          border: "1px dashed #D1D5DB",
          borderRadius: 8,
          padding: compact ? "14px 16px" : "18px 20px",
          color: "#6B7280",
          fontSize: 14,
        }}
      >
        No invoice or receipts for this order yet.
      </div>
    );
  }

  const onInvoice = async () => {
    try {
      setBusy("invoice");
      await downloadInvoicePdf({
        invoiceId: invId,
        saleOrderId,
        isCustomer,
        invoiceNumber: invoice?.invoice_number,
      });
      toast.success("Invoice downloaded.");
    } catch {
      /* WebService toasts */
    } finally {
      setBusy(null);
    }
  };

  const onReceipt = async (r: ReceiptDoc) => {
    if (!r.receipt_id) return;
    try {
      setBusy(`r-${r.receipt_id}`);
      await downloadReceiptPdf({
        receiptId: r.receipt_id,
        receiptNumber: r.receipt_number,
      });
      toast.success("Receipt downloaded.");
    } catch {
      /* WebService toasts */
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {invPresent && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            background: "#FFFBEB",
            border: "1px solid #FDE68A",
            borderRadius: 8,
            padding: "14px 16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <FaFileInvoice color="#B45309" size={18} style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 700, color: "#111827", fontSize: 15 }}>
                Invoice {invoice?.invoice_number ? `#${invoice.invoice_number}` : invId ? `#${invId}` : ""}
              </div>
              <div style={{ color: "#6B7280", fontSize: 13, marginTop: 4 }}>
                {invoice?.invoice_date ? `Date: ${invoice.invoice_date}` : null}
                {(invoice?.grand_total != null || invoice?.total_amount != null) && (
                  <span style={{ marginLeft: invoice?.invoice_date ? 12 : 0 }}>
                    Amount: {currency(invoice?.grand_total ?? invoice?.total_amount)}
                  </span>
                )}
                {invoice?.status ? (
                  <span style={{ marginLeft: 12 }}>Status: {invoice.status}</span>
                ) : null}
              </div>
            </div>
          </div>
          <button type="button" style={btnStyle} disabled={busy === "invoice"} onClick={onInvoice}>
            <FaFileDownload /> {busy === "invoice" ? "Downloading…" : "Download PDF"}
          </button>
        </div>
      )}

      {list.length > 0 && (
        <div>
          <div style={{ fontWeight: 600, color: "#111827", marginBottom: 10, fontSize: 15 }}>
            Receipts ({list.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {list.map((r) => (
              <div
                key={r.receipt_id ?? r.receipt_number}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  background: "#F0FDF4",
                  border: "1px solid #BBF7D0",
                  borderRadius: 8,
                  padding: "12px 16px",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <FaReceipt color="#059669" size={16} style={{ marginTop: 3 }} />
                  <div>
                    <div style={{ fontWeight: 600, color: "#111827", fontSize: 14 }}>
                      {r.receipt_number || `Receipt #${r.receipt_id}`}
                    </div>
                    <div style={{ color: "#6B7280", fontSize: 13, marginTop: 3 }}>
                      {currency(r.amount)}
                      {r.payment_method ? ` · ${r.payment_method}` : ""}
                      {r.paid_at ? ` · ${r.paid_at}` : ""}
                    </div>
                  </div>
                </div>
                {r.receipt_id != null && (
                  <button
                    type="button"
                    style={btnStyle}
                    disabled={busy === `r-${r.receipt_id}`}
                    onClick={() => onReceipt(r)}
                  >
                    <FaFileDownload />
                    {busy === `r-${r.receipt_id}` ? "Downloading…" : "Download PDF"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingDocuments;
